// Admin → Work Management → Workload & Health (Workload Intelligence Phase 1).
// Edits the workload/health part of the tenant Work policy (the same
// `work.tenant_work_policy` row Access & Assignment edits — one PUT of the
// whole policy). Progressive disclosure: the four everyday controls on top,
// the tuning knobs under "Advanced". The manager WORKLOAD visibility scope
// lives ONLY here; auth's management scope is synchronized from it by the
// server (`scope_sync` — never mirrored from the browser).
// Phase 2 adds a Capacity card below Advanced: the "Show utilization" policy
// switch (saved with the policy) + the per-person capacity table (saves on
// its own, PUT/DELETE per person).
// Phase 3 adds "Stale counts": calendar days (default) or business days (the
// company's rest days + public holidays from Time Off).
import React, { useEffect, useState } from 'react';
import toast from 'react-hot-toast';
import { useTranslation } from 'react-i18next';
import { Activity, AlertTriangle, CheckCircle2, ChevronDown, Info, Loader2, RefreshCw } from 'lucide-react';
import { RequirePermission } from '@/rbac/RequirePermission';
import { getApiErrorMessage } from '@/lib/apiError';
import { Button } from '@/features/shared/components/ui/button';
import { Badge } from '@/features/shared/components/ui/badge';
import { Input } from '@/features/shared/components/ui/input';
import { Switch } from '@/features/shared/components/ui/switch';
import { RadioGroup, RadioGroupItem } from '@/features/shared/components/ui/radio-group';
import { Label } from '@/features/shared/components/ui/label';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/features/shared/components/ui/collapsible';
import { EFFORT_SIZES, type EffortSize, type ManagerVisibilityScope, type ScopeSync, type WorkPolicy } from '@/services/workService';
import { runWorkloadSnapshot, type StaleBasis } from '@/services/workloadService';
import { useWorkPolicy } from '@/features/work/api/hooks';
import { WorkloadCapacitySection } from './WorkloadCapacitySection';

type K = (key: string, dflt: string, opts?: Record<string, unknown>) => string;

/** Defaults for fields an older backend may not return yet — the page never
 * renders `undefined` into a number input or a switch. */
export const WORKLOAD_DEFAULTS: Pick<WorkPolicy,
  'workload_enabled' | 'workload_use_effort' | 'workload_include_subitems' | 'workload_include_recurring'
  | 'workload_include_collaboration' | 'stale_enabled' | 'stale_after_days' | 'stale_basis' | 'due_soon_days'
  | 'manager_visibility_scope' | 'effort_weights' | 'workload_bands' | 'workload_show_utilization'> = {
  workload_enabled: false,
  workload_show_utilization: true,
  workload_use_effort: true,
  workload_include_subitems: true,
  workload_include_recurring: true,
  workload_include_collaboration: false,
  stale_enabled: true,
  stale_after_days: 5,
  stale_basis: 'calendar_days',
  due_soon_days: 3,
  manager_visibility_scope: 'DIRECT_REPORTS',
  effort_weights: { XS: 1, S: 2, M: 3, L: 5, XL: 8 },
  workload_bands: { normal: 3, high: 8, very_high: 13 },
};

export const withWorkloadDefaults = (p: WorkPolicy): WorkPolicy => ({
  ...WORKLOAD_DEFAULTS,
  ...p,
  effort_weights: { ...WORKLOAD_DEFAULTS.effort_weights, ...(p.effort_weights || {}) },
  workload_bands: { ...WORKLOAD_DEFAULTS.workload_bands, ...(p.workload_bands || {}) },
});

const clampInt = (v: string, min: number, max: number, fallback: number): number => {
  const n = parseInt(v, 10);
  if (Number.isNaN(n)) return fallback;
  return Math.max(min, Math.min(max, n));
};

/** Sync-state badge + retry, shared with Access & Assignment. */
export const ScopeSyncBadge: React.FC<{ scopeSync: ScopeSync | null; onRetry?: () => Promise<unknown>; className?: string }> = ({ scopeSync, onRetry, className = '' }) => {
  const { t } = useTranslation('work');
  const k: K = (key, dflt, opts = {}) => t(`policy_${key}`, { defaultValue: dflt, ...opts }) as string;
  const [busy, setBusy] = useState(false);
  if (!scopeSync) return null;
  const retry = async () => {
    if (!onRetry) return;
    setBusy(true);
    try {
      const next = (await onRetry()) as ScopeSync | undefined;
      if (next?.status === 'failed') toast.error(next.error || k('sync_retry_failed', 'Scope sync failed again.'));
      else toast.success(k('sync_retry_ok', 'Scope sync requested.'));
    } catch (e) {
      toast.error(getApiErrorMessage(e, k('sync_retry_failed', 'Scope sync failed again.')));
    } finally {
      setBusy(false);
    }
  };
  return (
    <div className={`flex items-center gap-2 flex-wrap ${className}`} data-testid="scope-sync">
      {scopeSync.status === 'ok' && (
        <Badge variant="success" className="gap-1"><CheckCircle2 className="w-3 h-3" /> {k('sync_ok', 'Auth scope in sync')}</Badge>
      )}
      {scopeSync.status === 'pending' && (
        <Badge variant="warning" className="gap-1"><Loader2 className="w-3 h-3 animate-spin" /> {k('sync_pending', 'Auth scope sync pending')}</Badge>
      )}
      {scopeSync.status === 'failed' && (
        <>
          <Badge variant="danger" className="gap-1"><AlertTriangle className="w-3 h-3" /> {k('sync_failed', 'Auth scope sync failed')}</Badge>
          {scopeSync.error && <span className="text-xs text-danger-strong" data-testid="scope-sync-error">{scopeSync.error}</span>}
        </>
      )}
      {scopeSync.status !== 'ok' && onRetry && (
        <Button size="sm" variant="outline" disabled={busy} onClick={retry}>
          {busy ? <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" /> : <RefreshCw className="w-3.5 h-3.5 mr-1" />}
          {k('sync_retry', 'Retry sync')}
        </Button>
      )}
    </div>
  );
};

const numCls = 'w-20 tabular-nums';

const WorkloadPolicyPage: React.FC = () => {
  const { t } = useTranslation('work');
  const k: K = (key, dflt, opts = {}) => t(`policy_${key}`, { defaultValue: dflt, ...opts }) as string;
  const { policy: saved, scopeSync, loading, error, reload, save, resync } = useWorkPolicy();
  const [draft, setDraft] = useState<WorkPolicy | null>(null);
  const [saving, setSaving] = useState(false);
  const [snapshotting, setSnapshotting] = useState(false);
  const takeSnapshot = async () => {
    setSnapshotting(true);
    try {
      const out = await runWorkloadSnapshot();
      toast.success(k('wl_snapshot_done', 'Snapshot recorded for {{count}} people.', { count: out.rows }));
    } catch (e) {
      toast.error(getApiErrorMessage(e, k('wl_snapshot_failed', 'Could not take a snapshot.')));
    } finally {
      setSnapshotting(false);
    }
  };
  const [advanced, setAdvanced] = useState(false);
  useEffect(() => { if (saved) setDraft(withWorkloadDefaults(saved)); }, [saved]);
  useEffect(() => {
    if (error) toast.error(getApiErrorMessage(error, k('load_failed', 'Could not load the work policy.')));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [error]);

  const set = <Key extends keyof WorkPolicy>(key: Key, value: WorkPolicy[Key]) => setDraft((p) => (p ? { ...p, [key]: value } : p));
  const base = saved ? withWorkloadDefaults(saved) : null;
  const dirty = !!draft && !!base && JSON.stringify(draft) !== JSON.stringify(base);

  const submit = async () => {
    if (!draft) return;
    setSaving(true);
    try {
      await save(draft);
      toast.success(k('saved', 'Work policy saved.'));
    } catch (e) {
      toast.error(getApiErrorMessage(e, k('save_failed', 'Could not save the work policy.')));
    } finally {
      setSaving(false);
    }
  };

  const scopes: Array<{ value: ManagerVisibilityScope; label: string; help: string }> = [
    { value: 'DIRECT_REPORTS', label: k('mgr_scope_direct', 'Direct reports'), help: k('wl_scope_direct_help', 'Managers see the workload of the people who report straight to them.') },
    { value: 'REPORTING_TREE', label: k('mgr_scope_tree', 'All reports'), help: k('wl_scope_tree_help', 'Everyone below the manager in the reporting tree.') },
    { value: 'DEPARTMENT', label: k('mgr_scope_department', 'Department'), help: k('wl_scope_department_help', 'Everyone in the manager’s department, reporting line or not.') },
    { value: 'TENANT', label: k('mgr_scope_tenant', 'Entire company'), help: k('wl_scope_tenant_help', 'Managers see workload across the whole company.') },
  ];
  const includes: Array<{ key: 'workload_include_subitems' | 'workload_include_recurring' | 'workload_include_collaboration'; label: string; help: string }> = [
    { key: 'workload_include_subitems', label: k('wl_include_subitems', 'Include sub-items'), help: k('wl_include_subitems_help', 'Count sub-items a person owns as part of their workload.') },
    { key: 'workload_include_recurring', label: k('wl_include_recurring', 'Include recurring work'), help: k('wl_include_recurring_help', 'Count occurrences generated by recurring templates.') },
    { key: 'workload_include_collaboration', label: k('wl_include_collaboration', 'Include collaboration'), help: k('wl_include_collaboration_help', 'Count work a person collaborates on but does not own (weighted lower).') },
  ];
  const sizeLabel: Record<EffortSize, string> = {
    XS: t('effort_xs', { defaultValue: 'XS · Very small' }) as string, S: t('effort_s', { defaultValue: 'S · Small' }) as string,
    M: t('effort_m', { defaultValue: 'M · Medium' }) as string, L: t('effort_l', { defaultValue: 'L · Large' }) as string,
    XL: t('effort_xl', { defaultValue: 'XL · Very large' }) as string,
  };

  return (
    <RequirePermission perm="work:manage">
      <div className="space-y-6 max-w-3xl">
        <div className="flex items-start gap-3">
          <span className="inline-flex items-center justify-center w-10 h-10 rounded-[12px] bg-orange-100 dark:bg-orange-500/[0.12] text-orange-700 dark:text-orange-300 shrink-0">
            <Activity className="w-5 h-5" />
          </span>
          <div className="min-w-0">
            <h1 className="text-h2 text-charcoal">{k('wl_title', 'Workload & Health')}</h1>
            <p className="text-sm text-muted-foreground">
              {k('wl_subtitle', 'How work health (overdue, at risk, stale, on hold, awaiting review) and each person’s relative workload are computed, and whose workload managers may see. These describe the work — never a person’s performance.')}
            </p>
            <ScopeSyncBadge scopeSync={scopeSync} onRetry={resync} className="mt-2" />
          </div>
        </div>

        {loading && !draft && <p className="text-sm text-muted-foreground">{t('common.loading', { defaultValue: 'Loading…' })}</p>}
        {!loading && !draft && (
          <div className="rounded-[10px] border border-border p-4 text-sm text-muted-foreground flex items-center justify-between gap-3">
            <span>{k('load_failed', 'Could not load the work policy.')}</span>
            <Button variant="outline" size="sm" onClick={() => reload()}>{k('retry', 'Retry')}</Button>
          </div>
        )}

        {draft && (
          <>
            <section className="rounded-[12px] border border-border divide-y divide-border">
              {/* Enable */}
              <div className="flex items-start justify-between gap-4 p-4">
                <div className="min-w-0">
                  <Label htmlFor="wl-enabled" className="text-sm font-medium text-charcoal">{k('wl_enabled', 'Enable workload intelligence')}</Label>
                  <p className="text-xs text-muted-foreground mt-0.5">{k('wl_enabled_help', 'Adds the Workload tab to Work, health chips on work items, and effort sizing. Off: none of this is computed or shown.')}</p>
                </div>
                <Switch id="wl-enabled" checked={draft.workload_enabled} disabled={saving} aria-label={k('wl_enabled', 'Enable workload intelligence')}
                  onCheckedChange={(v) => set('workload_enabled', v)} />
              </div>

              {/* Stale detection */}
              <div className="flex items-start justify-between gap-4 p-4">
                <div className="min-w-0">
                  <Label htmlFor="wl-stale" className="text-sm font-medium text-charcoal">{k('wl_stale', 'Stale detection')}</Label>
                  <p className="text-xs text-muted-foreground mt-0.5">{k('wl_stale_help', 'Flags a work item as stale when it has had no meaningful activity for the number of days below.')}</p>
                  <div className="mt-2 flex items-center gap-2 text-sm">
                    <Label htmlFor="wl-stale-days" className="font-normal">{k('wl_stale_after', 'Stale after')}</Label>
                    <Input id="wl-stale-days" type="number" min={1} max={90} className={numCls} value={draft.stale_after_days}
                      disabled={saving || !draft.stale_enabled}
                      onChange={(e) => set('stale_after_days', clampInt(e.target.value, 1, 90, draft.stale_after_days))} />
                    <span className="text-muted-foreground" data-testid="wl-stale-unit">
                      {draft.stale_basis === 'business_days' ? k('wl_business_days', 'business days') : k('wl_calendar_days', 'calendar days')}
                    </span>
                  </div>
                  <div className="mt-3">
                    <p className="text-sm text-charcoal">{k('wl_stale_basis', 'Stale counts')}</p>
                    <p className="text-xs text-muted-foreground mt-0.5">{k('wl_stale_basis_help', 'Business days use the company’s rest days and public holidays from Time Off.')}</p>
                    <RadioGroup className="mt-2 flex flex-wrap gap-4" value={draft.stale_basis || 'calendar_days'} disabled={saving || !draft.stale_enabled}
                      aria-label={k('wl_stale_basis', 'Stale counts')}
                      onValueChange={(v) => set('stale_basis', v as StaleBasis)}>
                      <div className="flex items-center gap-2">
                        <RadioGroupItem id="wl-stale-basis-calendar" value="calendar_days" />
                        <Label htmlFor="wl-stale-basis-calendar" className="text-sm font-normal">{k('wl_stale_basis_calendar', 'Calendar days')}</Label>
                      </div>
                      <div className="flex items-center gap-2">
                        <RadioGroupItem id="wl-stale-basis-business" value="business_days" />
                        <Label htmlFor="wl-stale-basis-business" className="text-sm font-normal">{k('wl_stale_basis_business', 'Business days')}</Label>
                      </div>
                    </RadioGroup>
                  </div>
                </div>
                <Switch id="wl-stale" checked={draft.stale_enabled} disabled={saving} aria-label={k('wl_stale', 'Stale detection')}
                  onCheckedChange={(v) => set('stale_enabled', v)} />
              </div>

              {/* Due soon */}
              <div className="p-4">
                <Label htmlFor="wl-due-soon" className="text-sm font-medium text-charcoal">{k('wl_due_soon', 'Due-soon window')}</Label>
                <p className="text-xs text-muted-foreground mt-0.5">{k('wl_due_soon_help', 'Work whose planned end falls within this many days counts as due soon (and at risk when progress is behind).')}</p>
                <div className="mt-2 flex items-center gap-2 text-sm">
                  <Input id="wl-due-soon" type="number" min={1} max={60} className={numCls} value={draft.due_soon_days} disabled={saving}
                    onChange={(e) => set('due_soon_days', clampInt(e.target.value, 1, 60, draft.due_soon_days))} />
                  <span className="text-muted-foreground">{k('wl_days', 'days')}</span>
                </div>
              </div>

              {/* Manager visibility scope */}
              <div className="p-4">
                <p className="text-sm font-medium text-charcoal">{k('wl_mgr_scope', 'Manager workload visibility scope')}</p>
                <p className="text-xs text-muted-foreground mt-0.5">{k('wl_mgr_scope_help', 'Whose workload a manager can see on the Workload tab. This is the Work policy; auth scope is synchronized from it.')}</p>
                <RadioGroup className="mt-3" value={draft.manager_visibility_scope} disabled={saving}
                  aria-label={k('wl_mgr_scope', 'Manager workload visibility scope')}
                  onValueChange={(v) => set('manager_visibility_scope', v as ManagerVisibilityScope)}>
                  {scopes.map((o) => (
                    <div key={o.value} className="flex items-start gap-2">
                      <RadioGroupItem id={`wl-scope-${o.value}`} value={o.value} className="mt-0.5" />
                      <Label htmlFor={`wl-scope-${o.value}`} className="text-sm font-normal">
                        {o.label}
                        <span className="block text-xs text-muted-foreground">{o.help}</span>
                      </Label>
                    </div>
                  ))}
                </RadioGroup>
              </div>
            </section>

            {/* Advanced */}
            <Collapsible open={advanced} onOpenChange={setAdvanced}>
              <section className="rounded-[12px] border border-border">
                <CollapsibleTrigger asChild>
                  <button type="button" className="flex w-full items-center justify-between gap-3 p-4 text-left" aria-expanded={advanced} data-testid="wl-advanced-toggle">
                    <span>
                      <span className="text-sm font-medium text-charcoal block">{k('wl_advanced', 'Advanced')}</span>
                      <span className="text-xs text-muted-foreground">{k('wl_advanced_help', 'What counts toward workload, effort weights and band thresholds.')}</span>
                    </span>
                    <ChevronDown className={`w-4 h-4 text-muted-foreground transition-transform ${advanced ? 'rotate-180' : ''}`} />
                  </button>
                </CollapsibleTrigger>
                <CollapsibleContent>
                  <div className="border-t border-border divide-y divide-border" data-testid="wl-advanced">
                    {includes.map((row) => (
                      <div key={row.key} className="flex items-start justify-between gap-4 p-4">
                        <div className="min-w-0">
                          <Label htmlFor={`wl-${row.key}`} className="text-sm font-medium text-charcoal">{row.label}</Label>
                          <p className="text-xs text-muted-foreground mt-0.5">{row.help}</p>
                        </div>
                        <Switch id={`wl-${row.key}`} checked={!!draft[row.key]} disabled={saving} aria-label={row.label}
                          onCheckedChange={(v) => set(row.key, v)} />
                      </div>
                    ))}
                    <div className="flex items-start justify-between gap-4 p-4">
                      <div className="min-w-0">
                        <Label htmlFor="wl-use-effort" className="text-sm font-medium text-charcoal">{k('wl_use_effort', 'Use effort sizes')}</Label>
                        <p className="text-xs text-muted-foreground mt-0.5">{k('wl_use_effort_help', 'Weight each item by its effort size (XS–XL). Unsized items use the Medium weight. Off: every item counts the same.')}</p>
                        {draft.workload_use_effort && (
                          <div className="mt-3">
                            <p className="text-xs font-medium text-charcoal">{k('wl_effort_weights', 'Effort weights')}</p>
                            <p className="text-xs text-muted-foreground">{k('wl_effort_weights_help', 'Relative weights, not hours.')}</p>
                            <div className="mt-2 grid grid-cols-2 sm:grid-cols-5 gap-2">
                              {EFFORT_SIZES.map((sz) => (
                                <div key={sz}>
                                  <Label htmlFor={`wl-weight-${sz}`} className="text-xs font-normal">{sizeLabel[sz]}</Label>
                                  <Input id={`wl-weight-${sz}`} type="number" min={0} max={100} step={0.5} className="tabular-nums" disabled={saving}
                                    value={draft.effort_weights[sz]}
                                    onChange={(e) => {
                                      const n = parseFloat(e.target.value);
                                      set('effort_weights', { ...draft.effort_weights, [sz]: Number.isNaN(n) ? 0 : Math.max(0, Math.min(100, n)) });
                                    }} />
                                </div>
                              ))}
                            </div>
                          </div>
                        )}
                      </div>
                      <Switch id="wl-use-effort" checked={draft.workload_use_effort} disabled={saving} aria-label={k('wl_use_effort', 'Use effort sizes')}
                        onCheckedChange={(v) => set('workload_use_effort', v)} />
                    </div>
                    <div className="p-4">
                      <p className="text-sm font-medium text-charcoal">{k('wl_bands', 'Workload bands')}</p>
                      <p className="text-xs text-muted-foreground mt-0.5">{k('wl_bands_help', 'Score at which each band starts. Below Normal is Low workload. Shown as bands with reasons — never as a percentage.')}</p>
                      <div className="mt-2 grid grid-cols-3 gap-2 max-w-md">
                        {(['normal', 'high', 'very_high'] as const).map((b) => (
                          <div key={b}>
                            <Label htmlFor={`wl-band-${b}`} className="text-xs font-normal">
                              {b === 'normal' ? t('band_normal', { defaultValue: 'Normal workload' }) : b === 'high' ? t('band_high', { defaultValue: 'High workload' }) : t('band_very_high', { defaultValue: 'Very high workload' })}
                            </Label>
                            <Input id={`wl-band-${b}`} type="number" min={0} max={1000} step={0.5} className="tabular-nums" disabled={saving}
                              value={draft.workload_bands[b]}
                              onChange={(e) => {
                                const n = parseFloat(e.target.value);
                                set('workload_bands', { ...draft.workload_bands, [b]: Number.isNaN(n) ? 0 : Math.max(0, n) });
                              }} />
                          </div>
                        ))}
                      </div>
                      {!(draft.workload_bands.normal <= draft.workload_bands.high && draft.workload_bands.high <= draft.workload_bands.very_high) && (
                        <p className="text-xs text-danger-strong mt-1" role="alert">{k('wl_bands_order', 'Thresholds must increase: Normal ≤ High ≤ Very high.')}</p>
                      )}
                    </div>
                  </div>
                </CollapsibleContent>
              </section>
            </Collapsible>

            {/* Capacity (Phase 2) */}
            <section className="rounded-[12px] border border-border divide-y divide-border" data-testid="wl-capacity-card">
              <div className="p-4">
                <p className="text-sm font-medium text-charcoal">{k('wl_capacity', 'Capacity')}</p>
                <p className="text-xs text-muted-foreground mt-0.5">
                  {k('wl_capacity_help', 'Capacity is in the same relative effort units as work sizes (XS=1 … XL=8 by default) per week. Utilization is shown only for people with capacity set.')}
                </p>
              </div>
              <div className="flex items-start justify-between gap-4 p-4">
                <div className="min-w-0">
                  <Label htmlFor="wl-show-utilization" className="text-sm font-medium text-charcoal">{k('wl_show_utilization', 'Show utilization')}</Label>
                  <p className="text-xs text-muted-foreground mt-0.5">{k('wl_show_utilization_help', 'Show the Capacity column, headroom sort and utilization percentage for people with capacity set. Off: capacity stays configured but nothing is shown. Saved with the policy.')}</p>
                </div>
                <Switch id="wl-show-utilization" checked={draft.workload_show_utilization !== false} disabled={saving}
                  aria-label={k('wl_show_utilization', 'Show utilization')}
                  onCheckedChange={(v) => set('workload_show_utilization', v)} />
              </div>
              <div className="p-4">
                <WorkloadCapacitySection disabled={saving} />
              </div>
            </section>

            {/* Trends (Phase 3): nightly snapshot + a manual one for day one */}
            <section className="rounded-[12px] border border-border" data-testid="wl-trends-card">
              <div className="flex items-start justify-between gap-4 p-4">
                <div className="min-w-0">
                  <p className="text-sm font-medium text-charcoal">{k('wl_trends', 'Trends')}</p>
                  <p className="text-xs text-muted-foreground mt-0.5">
                    {k('wl_trends_help', 'A snapshot of everyone’s counts is recorded every night at 00:30 and drawn as 30-day trends on the Workload tab. Take one now to start trends today.')}
                  </p>
                </div>
                <Button type="button" variant="outline" size="sm" disabled={saving || snapshotting} onClick={takeSnapshot} data-testid="wl-snapshot-now">
                  {snapshotting ? <Loader2 className="w-4 h-4 animate-spin" /> : null}
                  {k('wl_snapshot_now', 'Take a snapshot now')}
                </Button>
              </div>
            </section>

            <div className="flex items-start gap-2 rounded-[10px] bg-muted px-3 py-2 text-xs text-muted-foreground">
              <Info className="w-4 h-4 shrink-0 mt-0.5" />
              <span>{k('wl_note', 'Workload and health describe work items — counts, due dates and activity. They are not performance ratings and never appear in reviews.')}</span>
            </div>

            <div className="flex justify-end gap-2">
              <Button variant="ghost" disabled={!dirty || saving} onClick={() => base && setDraft(base)}>
                {t('common.cancel', { defaultValue: 'Cancel' })}
              </Button>
              <Button disabled={!dirty || saving || !(draft.workload_bands.normal <= draft.workload_bands.high && draft.workload_bands.high <= draft.workload_bands.very_high)} onClick={submit}>
                {saving ? k('saving', 'Saving…') : t('common.save', { defaultValue: 'Save' })}
              </Button>
            </div>
          </>
        )}
      </div>
    </RequirePermission>
  );
};

export default WorkloadPolicyPage;
